import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { toast } from "sonner";
import {
  FiTrendingUp,
  FiRefreshCw,
  FiCalendar,
  FiPercent,
  FiArrowUp,
  FiArrowDown,
  FiExternalLink,
  FiTrash2,
  FiPlus,
  FiEdit2,
  FiSave,
  FiX,
  FiDatabase,
  FiGlobe,
  FiBarChart2,
  FiClock,
  FiCheck,
  FiAlertCircle,
  FiLoader,
  FiInfo,
  FiMail,
  FiSend,
} from "@/lib/icons";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import { Tooltip } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ReferenceLine,
} from "recharts";

// ============================================
// TYPES
// ============================================

type CashRateEntry = {
  id: string;
  rate: number;
  effectiveDate: string;
  changeAmount: number | null;
  createdAt: string;
};

type RbaNewsItem = {
  id: string;
  guid: string;
  title: string;
  link: string;
  description: string | null;
  category: string | null;
  imageUrl: string | null;
  pubDate: string;
  fetchedAt: string;
};

type CronLogEntry = {
  id: string;
  jobName: string;
  trigger: "cron" | "manual";
  status: "running" | "success" | "error";
  resultSummary: string | null;
  resultData: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  startedAt: string;
  completedAt: string | null;
};

type Tab = "rates" | "news" | "jobs" | "email";
type ChartRange = "all" | "10y" | "5y" | "2y";

// ============================================
// JOB DEFINITIONS
// ============================================

type JobConfig = {
  key: string;
  label: string;
  /** Hour in AEST (24h) when the cron fires. null = manual only. */
  scheduleAestHour: number | null;
  scheduleLabel: string;
  icon: typeof FiPercent;
  description: ReactNode;
};

const JOB_CONFIGS: JobConfig[] = [
  {
    key: "scrape_rba_rates",
    label: "Scrape RBA Rates",
    scheduleAestHour: 10,
    scheduleLabel: "Daily 10:00 AM AEST",
    icon: FiPercent,
    description: (
      <div className="space-y-1.5">
        <p className="font-medium">Scrape RBA Cash Rate Decisions</p>
        <p>
          Visits the Reserve Bank of Australia website and pulls the latest
          official cash rate target. This keeps your rate history table
          up-to-date automatically so you always have the current rate
          for customer emails, calculators, and your rates page.
        </p>
        <p className="text-muted-foreground">
          The RBA meets ~8 times per year. Runs daily so new decisions
          are captured on the same day they're published.
        </p>
      </div>
    ),
  },
  {
    key: "sync_rba_news",
    label: "Sync RBA News",
    scheduleAestHour: null,
    scheduleLabel: "Manual only",
    icon: FiGlobe,
    description: (
      <div className="space-y-1.5">
        <p className="font-medium">Sync RBA News &amp; Media Releases</p>
        <p>
          Fetches the latest media releases, speeches, and bulletins from
          the RBA's official RSS feeds. These appear on your Market News
          page so your customers can see you're on top of the latest
          monetary policy updates.
        </p>
        <p className="text-muted-foreground">
          Run manually whenever you want to refresh. Each sync only
          fetches new articles — it won't duplicate existing ones.
        </p>
      </div>
    ),
  },
];

/**
 * Calculate next run time in UTC for a given AEST hour.
 * AEST = UTC+10 (ignoring daylight saving for simplicity).
 */
function getNextRunUTC(aestHour: number): Date {
  const now = new Date();
  // Convert AEST hour to UTC hour
  const utcHour = (aestHour - 10 + 24) % 24;
  const next = new Date(now);
  next.setUTCHours(utcHour, 0, 0, 0);
  if (next <= now) {
    next.setUTCDate(next.getUTCDate() + 1);
  }
  return next;
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return "Running now...";
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Hook that ticks every second and returns ms until next run. */
function useCountdown(aestHour: number | null): string | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (aestHour === null) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [aestHour]);
  if (aestHour === null) return null;
  const nextRun = getNextRunUTC(aestHour);
  return formatCountdown(nextRun.getTime() - now);
}

// ============================================
// JOB CARD (extracted so useCountdown hook can run per-card)
// ============================================

function JobCard({ job, lastRun }: { job: JobConfig; lastRun?: CronLogEntry }) {
  const countdown = useCountdown(job.scheduleAestHour);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className={cn(
            "p-2.5 rounded-full",
            !lastRun ? "bg-slate-100 text-slate-500" :
            lastRun.status === "success" ? "bg-green-100 text-green-600" :
            lastRun.status === "error" ? "bg-red-100 text-red-600" :
            "bg-amber-100 text-amber-600"
          )}>
            <job.icon className="w-4 h-4" />
          </div>
          {lastRun && (
            <Badge variant="secondary" className={cn(
              "text-xs ml-auto",
              lastRun.status === "success" ? "bg-green-100 text-green-700" :
              lastRun.status === "error" ? "bg-red-100 text-red-700" :
              "bg-amber-100 text-amber-700"
            )}>
              {lastRun.status === "success" && <FiCheck className="w-3 h-3 mr-1" />}
              {lastRun.status === "error" && <FiAlertCircle className="w-3 h-3 mr-1" />}
              {lastRun.status === "running" && <FiLoader className="w-3 h-3 mr-1 animate-spin" />}
              {lastRun.status}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <p className="font-medium text-sm">{job.label}</p>
          <Tooltip content={job.description} side="bottom" maxWidth="w-80 sm:w-96">
            <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
              <FiInfo className="w-3.5 h-3.5" />
            </button>
          </Tooltip>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{job.scheduleLabel}</p>
        {countdown && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <FiClock className="w-3 h-3 text-muted-foreground" />
            <p className="text-xs font-medium text-accent">Next run in {countdown}</p>
          </div>
        )}
        {lastRun ? (
          <div className="mt-2 pt-2 border-t text-xs text-muted-foreground space-y-0.5">
            <p>
              Last run: {new Date(lastRun.startedAt).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            </p>
            {lastRun.resultSummary && (
              <p className="text-foreground">{lastRun.resultSummary}</p>
            )}
            {lastRun.durationMs != null && (
              <p>{lastRun.durationMs < 1000 ? `${lastRun.durationMs}ms` : `${(lastRun.durationMs / 1000).toFixed(1)}s`}</p>
            )}
            <p className="capitalize">
              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                {lastRun.trigger}
              </Badge>
            </p>
          </div>
        ) : (
          <p className="mt-2 pt-2 border-t text-xs text-muted-foreground">Never run</p>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// COMPONENT
// ============================================

export function RBADataPage() {
  const [activeTab, setActiveTab] = useState<Tab>("rates");

  // Cash rates state
  const [rates, setRates] = useState<CashRateEntry[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(true);
  const [isScraping, setIsScraping] = useState(false);
  const [editingRate, setEditingRate] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ rate: "", effectiveDate: "", changeAmount: "" });
  const [showAddRate, setShowAddRate] = useState(false);
  const [addForm, setAddForm] = useState({ rate: "", effectiveDate: "", changeAmount: "", sendEmails: false });
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; rate: number; date: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // News state
  const [news, setNews] = useState<RbaNewsItem[]>([]);
  const [isLoadingNews, setIsLoadingNews] = useState(true);
  const [isSyncingNews, setIsSyncingNews] = useState(false);

  // Cron logs state
  const [cronLogs, setCronLogs] = useState<CronLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);

  // Email preview state
  const [emailPreviewHtml, setEmailPreviewHtml] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Chart state
  const [chartRange, setChartRange] = useState<ChartRange>("all");

  // ============================================
  // FETCH DATA
  // ============================================

  const fetchRates = useCallback(async () => {
    setIsLoadingRates(true);
    try {
      const res = await fetch("/api/admin/cash-rate-history", { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as CashRateEntry[];
        setRates(data);
      }
    } catch (err) {
      console.error("Failed to fetch rates:", err);
    } finally {
      setIsLoadingRates(false);
    }
  }, []);

  const fetchNews = useCallback(async () => {
    setIsLoadingNews(true);
    try {
      const res = await fetch("/api/market/rba-news", { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as RbaNewsItem[];
        setNews(data);
      }
    } catch (err) {
      console.error("Failed to fetch news:", err);
    } finally {
      setIsLoadingNews(false);
    }
  }, []);

  const fetchCronLogs = useCallback(async () => {
    setIsLoadingLogs(true);
    try {
      const res = await fetch("/api/admin/cron-logs", { credentials: "include" });
      if (res.ok) {
        const data = (await res.json()) as CronLogEntry[];
        setCronLogs(data);
      }
    } catch (err) {
      console.error("Failed to fetch cron logs:", err);
    } finally {
      setIsLoadingLogs(false);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    fetchNews();
    fetchCronLogs();
  }, [fetchRates, fetchNews, fetchCronLogs]);

  // ============================================
  // RATE ACTIONS
  // ============================================

  const handleScrapeRates = async () => {
    setIsScraping(true);
    try {
      const res = await fetch("/api/admin/market/scrape-rates", {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { data: { scraped: number; newEntries: number; latestRate: number | null; latestDate: string | null }; message: string };
      if (res.ok) {
        toast.success(data.message || `Scraped ${data.data.newEntries} new entries`);
        fetchRates();
        fetchCronLogs();
      } else {
        toast.error("Failed to scrape rates");
      }
    } catch (err) {
      toast.error("Error scraping rates");
    } finally {
      setIsScraping(false);
    }
  };

  const handleAddRate = async () => {
    if (!addForm.rate || !addForm.effectiveDate) {
      toast.error("Rate and effective date are required");
      return;
    }
    try {
      const changeAmount = addForm.changeAmount ? parseFloat(addForm.changeAmount) : null;
      const res = await fetch("/api/admin/cash-rate-history", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rate: parseFloat(addForm.rate),
          effectiveDate: addForm.effectiveDate,
          changeAmount,
          sendEmails: addForm.sendEmails,
        }),
      });
      const data = await res.json() as {
        entry?: unknown;
        emailResult?: { queued: boolean; reason?: string } | { error: string } | null;
        error?: string;
      };
      if (res.ok) {
        if (data.emailResult && "queued" in data.emailResult) {
          toast.success(
            data.emailResult.queued
              ? "Rate added. Rate alert emails are being sent in the background."
              : "Rate added. Rate alert emails are already being sent.",
          );
        } else if (data.emailResult && "error" in data.emailResult) {
          toast.error(`Rate added but emails could not be queued: ${data.emailResult.error}`);
        } else {
          toast.success("Rate entry added");
        }
        setShowAddRate(false);
        setAddForm({ rate: "", effectiveDate: "", changeAmount: "", sendEmails: false });
        fetchRates();
        fetchCronLogs();
      } else {
        toast.error(data.error || "Failed to add rate entry");
      }
    } catch (err) {
      toast.error("Error adding rate entry");
    }
  };

  const handleEditRate = (entry: CashRateEntry) => {
    setEditingRate(entry.id);
    const d = new Date(entry.effectiveDate);
    setEditForm({
      rate: String(entry.rate),
      effectiveDate: d.toISOString().split("T")[0],
      changeAmount: entry.changeAmount != null ? String(entry.changeAmount) : "",
    });
  };

  const handleSaveRate = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/cash-rate-history/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rate: parseFloat(editForm.rate),
          effectiveDate: editForm.effectiveDate,
          changeAmount: editForm.changeAmount ? parseFloat(editForm.changeAmount) : null,
        }),
      });
      if (res.ok) {
        toast.success("Rate entry updated");
        setEditingRate(null);
        fetchRates();
      } else {
        toast.error("Failed to update rate entry");
      }
    } catch (err) {
      toast.error("Error updating rate entry");
    }
  };

  const handleDeleteRate = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`/api/admin/cash-rate-history/${deleteConfirm.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Rate entry deleted");
        setDeleteConfirm(null);
        fetchRates();
      } else {
        toast.error("Failed to delete rate entry");
      }
    } catch (err) {
      toast.error("Error deleting rate entry");
    } finally {
      setIsDeleting(false);
    }
  };

  // ============================================
  // NEWS ACTIONS
  // ============================================

  const handleSyncNews = async () => {
    setIsSyncingNews(true);
    try {
      const res = await fetch("/api/admin/market/sync", {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { message: string };
      if (res.ok) {
        toast.success(data.message || "News synced");
        fetchNews();
        fetchCronLogs();
      } else {
        toast.error("Failed to sync news");
      }
    } catch (err) {
      toast.error("Error syncing news");
    } finally {
      setIsSyncingNews(false);
    }
  };

  const handleResyncNews = async () => {
    if (!confirm("This will clear all existing news and re-fetch from RBA. Continue?")) return;
    setIsSyncingNews(true);
    try {
      const res = await fetch("/api/admin/market/resync", {
        method: "POST",
        credentials: "include",
      });
      const data = (await res.json()) as { message: string };
      if (res.ok) {
        toast.success(data.message || "News re-synced");
        fetchNews();
        fetchCronLogs();
      } else {
        toast.error("Failed to resync news");
      }
    } catch (err) {
      toast.error("Error resyncing news");
    } finally {
      setIsSyncingNews(false);
    }
  };

  // ============================================
  // DERIVED DATA
  // ============================================

  const latestRate = rates[0];
  const previousRate = rates[1];
  const rateDirection = latestRate && previousRate
    ? latestRate.rate > previousRate.rate ? "up" : latestRate.rate < previousRate.rate ? "down" : "hold"
    : "hold";

  // Chart data — sorted chronologically (oldest first) and filtered by range
  const chartData = useMemo(() => {
    if (rates.length === 0) return [];

    // Rates come sorted desc from API, reverse for chronological
    const sorted = [...rates].reverse();

    let cutoff: Date | null = null;
    if (chartRange !== "all") {
      const years = chartRange === "10y" ? 10 : chartRange === "5y" ? 5 : 2;
      cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - years);
    }

    return sorted
      .filter((r) => {
        if (!cutoff) return true;
        return new Date(r.effectiveDate) >= cutoff;
      })
      .map((r) => ({
        date: new Date(r.effectiveDate).toLocaleDateString("en-AU", {
          month: "short",
          year: "numeric",
        }),
        fullDate: new Date(r.effectiveDate).toLocaleDateString("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        rate: r.rate,
        change: r.changeAmount,
      }));
  }, [rates, chartRange]);

  const categoryColors: Record<string, string> = {
    "RBA Media": "bg-blue-100 text-blue-700",
    "RBA Speeches": "bg-purple-100 text-purple-700",
    "RBA Bulletin": "bg-indigo-100 text-indigo-700",
  };

  // ============================================
  // EMAIL PREVIEW / SEND
  // ============================================

  const emailRateData = useMemo(() => {
    if (!latestRate || !previousRate) return null;
    return {
      newRate: latestRate.rate,
      changeAmount: latestRate.changeAmount ?? latestRate.rate - previousRate.rate,
      previousRate: previousRate.rate,
      effectiveDate: new Date(latestRate.effectiveDate).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }),
    };
  }, [latestRate, previousRate]);

  const fetchEmailPreview = useCallback(async () => {
    if (!emailRateData) return;
    setIsLoadingPreview(true);
    try {
      const res = await fetch("/api/admin/market/rate-email-preview", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...emailRateData, firstName: "Sacha" }),
      });
      if (res.ok) {
        const data = (await res.json()) as { html: string };
        setEmailPreviewHtml(data.html);
      } else {
        toast.error("Failed to load email preview");
      }
    } catch {
      toast.error("Error loading preview");
    } finally {
      setIsLoadingPreview(false);
    }
  }, [emailRateData]);

  useEffect(() => {
    if (activeTab === "email" && emailRateData) {
      fetchEmailPreview();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, emailRateData]);

  const handleSendTestEmail = async () => {
    if (!emailRateData || !testEmail.trim()) {
      toast.error("Enter a test email address");
      return;
    }
    setIsSendingEmail(true);
    try {
      const res = await fetch("/api/admin/market/rate-email-send", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...emailRateData, testEmail: testEmail.trim() }),
      });
      const data = (await res.json()) as { message?: string };
      if (res.ok) {
        toast.success(`Test email sent to ${testEmail.trim()}`);
      } else {
        toast.error(data.message || "Failed to send test email");
      }
    } catch {
      toast.error("Error sending test email");
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleSendToSubscribers = async () => {
    if (!emailRateData) return;
    if (!confirm("Send rate change email to ALL subscribers with rate alerts enabled, plus customers who haven't opted out? It sends in the background over the next few hours.")) return;
    setIsSendingEmail(true);
    try {
      const res = await fetch("/api/admin/market/rate-email-send", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(emailRateData),
      });
      const data = (await res.json()) as { message?: string };
      if (res.ok) {
        toast.success(data.message || "Rate alert emails queued");
      } else {
        toast.error(data.message || "Failed to send emails");
      }
    } catch {
      toast.error("Error sending emails");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            RBA Data
          </h1>
          <p className="text-muted-foreground mt-1">
            Cash rate history, RBA news, and market data sync
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className={cn(
                "p-3 rounded-full",
                rateDirection === "up" ? "bg-red-100 text-red-600" :
                rateDirection === "down" ? "bg-green-100 text-green-600" :
                "bg-slate-100 text-slate-600"
              )}>
                {rateDirection === "up" ? <FiArrowUp className="w-5 h-5" /> :
                 rateDirection === "down" ? <FiArrowDown className="w-5 h-5" /> :
                 <FiPercent className="w-5 h-5" />}
              </div>
              {latestRate?.changeAmount != null && latestRate.changeAmount !== 0 && (
                <Badge variant="secondary" className={cn(
                  latestRate.changeAmount > 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                )}>
                  {latestRate.changeAmount > 0 ? "+" : ""}{latestRate.changeAmount.toFixed(2)}%
                </Badge>
              )}
            </div>
            <div className="mt-4">
              <p className="text-3xl font-semibold">
                {latestRate ? `${latestRate.rate.toFixed(2)}%` : "-"}
              </p>
              <p className="text-sm text-muted-foreground">Current Cash Rate</p>
              {latestRate && (
                <p className="text-xs text-muted-foreground mt-1">
                  Effective {new Date(latestRate.effectiveDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                <FiDatabase className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-semibold">{rates.length}</p>
              <p className="text-sm text-muted-foreground">Rate Decisions Stored</p>
              <p className="text-xs text-muted-foreground mt-1">
                {rates.length > 0
                  ? `${new Date(rates[rates.length - 1]?.effectiveDate).getFullYear()} - ${new Date(rates[0]?.effectiveDate).getFullYear()}`
                  : "No data"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                <FiGlobe className="w-5 h-5" />
              </div>
            </div>
            <div className="mt-4">
              <p className="text-3xl font-semibold">{news.length}</p>
              <p className="text-sm text-muted-foreground">RBA News Articles</p>
              {news[0] && (
                <p className="text-xs text-muted-foreground mt-1">
                  Latest: {new Date(news[0].pubDate).toLocaleDateString("en-AU")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab("rates")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
            activeTab === "rates"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <FiTrendingUp className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          Cash Rate History
        </button>
        <button
          onClick={() => setActiveTab("news")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
            activeTab === "news"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <FiGlobe className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          RBA News ({news.length})
        </button>
        <button
          onClick={() => setActiveTab("email")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
            activeTab === "email"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <FiMail className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          Rate Email
        </button>
        <button
          onClick={() => setActiveTab("jobs")}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px",
            activeTab === "jobs"
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <FiClock className="inline w-4 h-4 mr-1.5 -mt-0.5" />
          Cron Jobs
        </button>
      </div>

      {/* ============================================ */}
      {/* CASH RATE HISTORY TAB */}
      {/* ============================================ */}
      {activeTab === "rates" && (
        <div className="space-y-4">
          {/* Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleScrapeRates}
                  disabled={isScraping}
                  className="btn-pill"
                >
                  <FiRefreshCw className={cn("mr-2 h-4 w-4", isScraping && "animate-spin")} />
                  {isScraping ? "Scraping RBA..." : "Scrape RBA Rates"}
                </Button>
                <Button
                  variant="outline"
                  className="btn-pill"
                  onClick={() => setShowAddRate(!showAddRate)}
                >
                  <FiPlus className="mr-2 h-4 w-4" />
                  Add Manually
                </Button>
                <a
                  href="https://www.rba.gov.au/statistics/cash-rate/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3"
                >
                  <FiExternalLink className="w-4 h-4" />
                  View RBA Source
                </a>
              </div>
            </CardContent>
          </Card>

          {/* Add Rate Form */}
          {showAddRate && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Add Rate Entry</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-3 items-end">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Effective Date</label>
                    <Input
                      type="date"
                      value={addForm.effectiveDate}
                      onChange={(e) => setAddForm(f => ({ ...f, effectiveDate: e.target.value }))}
                      className="w-44"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Rate %</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="4.35"
                      value={addForm.rate}
                      onChange={(e) => setAddForm(f => ({ ...f, rate: e.target.value }))}
                      className="w-28"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Change %</label>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="+0.25"
                      value={addForm.changeAmount}
                      onChange={(e) => setAddForm(f => ({ ...f, changeAmount: e.target.value }))}
                      className="w-28"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={addForm.sendEmails}
                      onChange={(e) => setAddForm(f => ({ ...f, sendEmails: e.target.checked }))}
                      className="rounded border-gray-300 text-primary focus:ring-primary h-4 w-4"
                    />
                    <span className="text-xs flex items-center gap-1">
                      <FiMail className="h-3.5 w-3.5" />
                      Send rate alert emails
                    </span>
                  </label>
                  <Button onClick={handleAddRate} size="sm">
                    <FiSave className="mr-1.5 h-4 w-4" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowAddRate(false); setAddForm({ rate: "", effectiveDate: "", changeAmount: "", sendEmails: false }); }}
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Rate History Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Interest Rate Decisions</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingRates ? (
                <div className="space-y-3">
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </div>
              ) : rates.length === 0 ? (
                <div className="text-center py-12">
                  <FiPercent className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No rate data yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click "Scrape RBA Rates" to fetch the latest data
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[480px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b text-left">
                        <th className="py-3 pr-4 pl-4 font-medium text-muted-foreground">Effective Date</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Change</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Cash Rate Target</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rates.map((entry) => (
                        <tr key={entry.id} className="border-b last:border-0 hover:bg-muted/50">
                          {editingRate === entry.id ? (
                            <>
                              <td className="py-3 pr-4 pl-4">
                                <Input
                                  type="date"
                                  value={editForm.effectiveDate}
                                  onChange={(e) => setEditForm(f => ({ ...f, effectiveDate: e.target.value }))}
                                  className="w-44 h-8 text-sm"
                                />
                              </td>
                              <td className="py-3 pr-4">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editForm.changeAmount}
                                  onChange={(e) => setEditForm(f => ({ ...f, changeAmount: e.target.value }))}
                                  className="w-24 h-8 text-sm"
                                />
                              </td>
                              <td className="py-3 pr-4">
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={editForm.rate}
                                  onChange={(e) => setEditForm(f => ({ ...f, rate: e.target.value }))}
                                  className="w-24 h-8 text-sm"
                                />
                              </td>
                              <td className="py-3 pr-4 text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="ghost" onClick={() => handleSaveRate(entry.id)}>
                                    <FiSave className="h-4 w-4" />
                                  </Button>
                                  <Button size="sm" variant="ghost" onClick={() => setEditingRate(null)}>
                                    <FiX className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="py-3 pr-4 pl-4">
                                <div className="flex items-center gap-2">
                                  <FiCalendar className="w-4 h-4 text-muted-foreground" />
                                  {new Date(entry.effectiveDate).toLocaleDateString("en-AU", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </div>
                              </td>
                              <td className="py-3 pr-4">
                                {entry.changeAmount != null ? (
                                  <Badge
                                    variant="secondary"
                                    className={cn(
                                      "font-mono",
                                      entry.changeAmount > 0 ? "bg-red-100 text-red-700" :
                                      entry.changeAmount < 0 ? "bg-green-100 text-green-700" :
                                      "bg-slate-100 text-slate-600"
                                    )}
                                  >
                                    {entry.changeAmount > 0 && "+"}
                                    {entry.changeAmount === 0 ? "No change" : `${entry.changeAmount.toFixed(2)}%`}
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                              <td className="py-3 pr-4">
                                <span className="font-semibold text-base">{entry.rate.toFixed(2)}%</span>
                              </td>
                              <td className="py-3 pr-4 text-right">
                                <div className="flex gap-1 justify-end">
                                  <Button size="sm" variant="ghost" onClick={() => handleEditRate(entry)}>
                                    <FiEdit2 className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="text-destructive"
                                    onClick={() => setDeleteConfirm({
                                      id: entry.id,
                                      rate: entry.rate,
                                      date: new Date(entry.effectiveDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }),
                                    })}
                                  >
                                    <FiTrash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cash Rate Target Chart */}
          {rates.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FiBarChart2 className="w-4 h-4" />
                    Cash Rate Target Over Time
                  </CardTitle>
                  <div className="flex gap-1">
                    {(["all", "10y", "5y", "2y"] as ChartRange[]).map((range) => (
                      <Button
                        key={range}
                        size="sm"
                        variant={chartRange === range ? "default" : "outline"}
                        className="h-7 px-3 text-xs"
                        onClick={() => setChartRange(range)}
                      >
                        {range === "all" ? "All" : range.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="rateGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1a6fa3" stopOpacity={0.25} />
                          <stop offset="95%" stopColor="#1a6fa3" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e1db" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: "#00365a" }}
                        tickLine={false}
                        axisLine={{ stroke: "#e5e1db" }}
                        interval="preserveStartEnd"
                        minTickGap={60}
                      />
                      <YAxis
                        domain={["dataMin - 0.5", "dataMax + 0.5"]}
                        tick={{ fontSize: 11, fill: "#00365a" }}
                        tickLine={false}
                        axisLine={{ stroke: "#e5e1db" }}
                        tickFormatter={(v: number) => `${v.toFixed(1)}%`}
                        width={55}
                      />
                      <RechartsTooltip
                        content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const d = payload[0].payload as (typeof chartData)[0];
                          return (
                            <div className="bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
                              <p className="font-medium text-foreground">{d.fullDate}</p>
                              <p className="text-accent font-semibold text-lg">{d.rate.toFixed(2)}%</p>
                              {d.change != null && d.change !== 0 && (
                                <p className={cn(
                                  "text-xs font-medium",
                                  d.change > 0 ? "text-red-600" : "text-green-600"
                                )}>
                                  {d.change > 0 ? "+" : ""}{d.change.toFixed(2)}% change
                                </p>
                              )}
                            </div>
                          );
                        }}
                      />
                      <ReferenceLine
                        y={latestRate?.rate}
                        stroke="#00365a"
                        strokeDasharray="4 4"
                        strokeOpacity={0.3}
                        label={{
                          value: `Current: ${latestRate?.rate.toFixed(2)}%`,
                          position: "right",
                          fill: "#00365a",
                          fontSize: 11,
                        }}
                      />
                      <Area
                        type="stepAfter"
                        dataKey="rate"
                        stroke="#1a6fa3"
                        strokeWidth={2}
                        fill="url(#rateGradient)"
                        dot={false}
                        activeDot={{ r: 5, fill: "#00263e", stroke: "#fff", strokeWidth: 2 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {chartData.length} rate decisions &bull; {chartData[0]?.fullDate} to {chartData[chartData.length - 1]?.fullDate}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* RBA NEWS TAB */}
      {/* ============================================ */}
      {activeTab === "news" && (
        <div className="space-y-4">
          {/* Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleSyncNews}
                  disabled={isSyncingNews}
                  className="btn-pill"
                >
                  <FiRefreshCw className={cn("mr-2 h-4 w-4", isSyncingNews && "animate-spin")} />
                  {isSyncingNews ? "Syncing..." : "Sync News (Incremental)"}
                </Button>
                <Button
                  variant="outline"
                  className="btn-pill"
                  onClick={handleResyncNews}
                  disabled={isSyncingNews}
                >
                  <FiRefreshCw className="mr-2 h-4 w-4" />
                  Full Resync
                </Button>
                <a
                  href="https://www.rba.gov.au/media-releases/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors px-3"
                >
                  <FiExternalLink className="w-4 h-4" />
                  View RBA Media Releases
                </a>
              </div>
            </CardContent>
          </Card>

          {/* News List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">RBA News Articles</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingNews ? (
                <div className="space-y-3">
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </div>
              ) : news.length === 0 ? (
                <div className="text-center py-12">
                  <FiGlobe className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No news articles yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Click "Sync News" to fetch the latest RBA releases
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {news.slice(0, 50).map((item) => (
                    <a
                      key={item.id}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {item.category && (
                              <Badge
                                variant="secondary"
                                className={cn("text-xs", categoryColors[item.category] || "bg-muted")}
                              >
                                {item.category}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(item.pubDate).toLocaleDateString("en-AU", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                          </div>
                          <p className="font-medium group-hover:text-primary transition-colors">
                            {item.title}
                          </p>
                          {item.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {item.description}
                            </p>
                          )}
                        </div>
                        <FiExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </a>
                  ))}
                  {news.length > 50 && (
                    <p className="text-sm text-muted-foreground text-center pt-2">
                      Showing 50 of {news.length} articles
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* ============================================ */}
      {/* RATE EMAIL TAB */}
      {/* ============================================ */}
      {activeTab === "email" && (
        <div className="space-y-4">
          {!emailRateData ? (
            <Card>
              <CardContent className="p-8 text-center">
                <FiMail className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">Need at least 2 rate entries to generate an email</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Scrape rates first to populate the data.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Rate summary cards */}
              <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Previous Rate</p>
                    <p className="text-2xl font-bold text-foreground">{emailRateData.previousRate.toFixed(2)}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Change</p>
                    <p className={cn(
                      "text-2xl font-bold",
                      emailRateData.changeAmount > 0 ? "text-red-600" : emailRateData.changeAmount < 0 ? "text-green-600" : "text-muted-foreground"
                    )}>
                      {emailRateData.changeAmount > 0 ? "+" : ""}{emailRateData.changeAmount.toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">New Rate</p>
                    <p className="text-2xl font-bold text-accent">{emailRateData.newRate.toFixed(2)}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Effective</p>
                    <p className="text-sm font-semibold text-foreground mt-1">{emailRateData.effectiveDate}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Send controls */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FiInfo className="w-4 h-4 flex-shrink-0" />
                      <span>
                        This email will be sent to all subscribers with <strong className="text-foreground">rate alerts</strong> enabled,
                        plus customers who haven&apos;t opted out. It sends in the background over a few hours.
                        Each recipient gets a personalised greeting and their own unsubscribe link.
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="email"
                        placeholder="Test email address"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        className="w-60 h-9 text-sm"
                        onKeyDown={(e) => e.key === "Enter" && handleSendTestEmail()}
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        className="btn-pill"
                        onClick={handleSendTestEmail}
                        disabled={isSendingEmail || !testEmail.trim()}
                      >
                        <FiSend className={cn("mr-1.5 h-3.5 w-3.5", isSendingEmail && "animate-pulse")} />
                        Send Test
                      </Button>
                      <div className="flex-1" />
                      <Button
                        size="sm"
                        className="btn-pill"
                        onClick={handleSendToSubscribers}
                        disabled={isSendingEmail}
                      >
                        <FiMail className={cn("mr-1.5 h-3.5 w-3.5", isSendingEmail && "animate-pulse")} />
                        Send to All Subscribers
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Preview */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Email Preview</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setEmailPreviewHtml(null); fetchEmailPreview(); }}
                      disabled={isLoadingPreview}
                      className="h-8"
                    >
                      <FiRefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isLoadingPreview && "animate-spin")} />
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {isLoadingPreview ? (
                    <div className="flex items-center justify-center py-20">
                      <FiLoader className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : emailPreviewHtml ? (
                    <div className="border rounded-lg overflow-hidden bg-[#f5f1eb]">
                      <iframe
                        srcDoc={emailPreviewHtml}
                        title="Rate change email preview"
                        className="w-full border-0"
                        style={{ height: "1000px" }}
                        sandbox="allow-same-origin"
                      />
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      Failed to load preview
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* CRON JOBS TAB */}
      {/* ============================================ */}
      {activeTab === "jobs" && (
        <div className="space-y-4">
          {/* Job Status Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {JOB_CONFIGS.map((job) => (
              <JobCard key={job.key} job={job} lastRun={cronLogs.find((l) => l.jobName === job.key)} />
            ))}
          </div>

          {/* Run History */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Run History</CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchCronLogs}
                  disabled={isLoadingLogs}
                  className="h-8"
                >
                  <FiRefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isLoadingLogs && "animate-spin")} />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingLogs ? (
                <div className="space-y-3">
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </div>
              ) : cronLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FiClock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No cron runs recorded yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Runs will appear here after scraping rates or syncing news
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b text-left">
                        <th className="py-3 pl-4 pr-4 font-medium text-muted-foreground">Status</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Job</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Trigger</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Result</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Duration</th>
                        <th className="py-3 pr-4 font-medium text-muted-foreground">Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cronLogs.filter((l) => l.jobName === "scrape_rba_rates" || l.jobName === "sync_rba_news" || l.jobName === "rate_change_email").map((log) => (
                        <tr key={log.id} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="py-3 pl-4 pr-4">
                            <div className={cn(
                              "w-2.5 h-2.5 rounded-full",
                              log.status === "success" ? "bg-green-500" :
                              log.status === "error" ? "bg-red-500" :
                              "bg-amber-500 animate-pulse"
                            )} />
                          </td>
                          <td className="py-3 pr-4">
                            <span className="font-medium">
                              {log.jobName === "scrape_rba_rates" ? "Scrape RBA Rates" :
                               log.jobName === "sync_rba_news" ? "Sync RBA News" :
                               log.jobName === "rate_change_email" ? "Rate Change Email" :
                               log.jobName === "process_automations" ? "Process Automations" :
                               log.jobName === "send_scheduled_emails" ? "Send Emails" :
                               log.jobName}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <Badge variant="outline" className="text-xs">
                              {log.trigger}
                            </Badge>
                          </td>
                          <td className="py-3 pr-4">
                            {log.status === "error" ? (
                              <span className="text-red-600 text-xs">{log.errorMessage?.slice(0, 60)}</span>
                            ) : (
                              <span className="text-xs text-muted-foreground">{log.resultSummary || "-"}</span>
                            )}
                          </td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground font-mono">
                            {log.durationMs != null
                              ? log.durationMs < 1000 ? `${log.durationMs}ms` : `${(log.durationMs / 1000).toFixed(1)}s`
                              : "-"}
                          </td>
                          <td className="py-3 pr-4 text-xs text-muted-foreground whitespace-nowrap">
                            {new Date(log.startedAt).toLocaleDateString("en-AU", {
                              day: "numeric",
                              month: "short",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <FiAlertCircle className="h-5 w-5" />
              Delete Rate Decision
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the rate decision for{" "}
              <strong>{deleteConfirm?.date}</strong> at{" "}
              <strong>{deleteConfirm?.rate}%</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(null)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteRate}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <FiLoader className="mr-1.5 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <FiTrash2 className="mr-1.5 h-4 w-4" />
                  Delete
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
